import { useEffect, useState } from "react";
import SettingsStore from "@/stores/SettingsStore";
import ApiKeyStore from "@/stores/ApiKeyStore";
import SchadenKIStore from "@/stores/SchadenKIStore";
import MultiadapterStore from "@/stores/MultiadapterStore";
import DatenadapterStore from "@/stores/DatenadapterStore";
import BlockbrainStore from "@/stores/BlockbrainStore";
import { HeygenAvatarSettings } from "./heygen-avatar/HeygenAvatarSettings";
import { InteractiveSessionSettings } from "./interactive-session/InteractiveSessionSettings";
import { RealtimeApiSettings } from "./realtime-api/RealtimeApiSettings";
import { SaveSettingsButton } from "./SaveSettingsButton";
import { SaveApiKeyButton } from "./SaveApiKeyButton";
import { SaveSchadenKIButton } from "./SaveSchadenKIButton";
import { SaveBlockbrainButton } from "./SaveBlockbrainButton";
import { InfoSection } from "./heygen-account/InfoSection";
import { KnowledgeBaseSettings } from "./knowledge-base/KnowledgeBaseSettings";
import { SchadenKISettings } from "./schaden-ki/SchadenKISettings";
import { BlockbrainSettings } from "./blockbrain/BlockbrainSettings";
import { AnalyticsSettings } from "./analytics/AnalyticsSettings";
import { BrandingSettings } from "./branding/BrandingSettings";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useTranslation } from "react-i18next";
import { useUnsavedChanges } from "@/hooks/useUnsavedChanges";
import { env } from "@/lib/env";
import { featureFlags } from "@/lib/featureFlags";

export function SettingsSection() {
  const { t } = useTranslation();
  const fetchSettings = SettingsStore(state => state.fetchSettings);
  const setIsLoading = SettingsStore(state => state.setIsLoading);
  const hasSettingsChanges = SettingsStore(state => state.hasChanges);
  const useKnowledgeBase = SettingsStore(state => state.useKnowledgeBase);
  const useSchadenKI = SettingsStore(state => state.useSchadenKI);
  const [currentTab, setCurrentTab] = useState("heygen-avatar");

  // Track API key changes from the store
  const hasApiKeyChanges = ApiKeyStore(state => state.hasChanges);
  const hasSchadenKIChanges = SchadenKIStore(state => state.hasChanges);
  const hasMultiadapterChanges = MultiadapterStore(state => state.hasChanges);
  const hasDatenadapterChanges = DatenadapterStore(state => state.hasChanges);
  const hasBlockbrainChanges = BlockbrainStore(state => state.hasChanges);

  // Combine all change states
  const hasAnyChanges = hasSettingsChanges || hasApiKeyChanges || hasSchadenKIChanges || hasMultiadapterChanges || hasDatenadapterChanges || hasBlockbrainChanges;

  // Use the unsaved changes hook to warn users when leaving with unsaved changes
  useUnsavedChanges({
    hasUnsavedChanges: hasAnyChanges,
    message: t('settings.unsavedChangesWarning', 'You have unsaved changes. Are you sure you want to leave?')
  });

  useEffect(() => {
    let isMounted = true;

    const loadSettings = async () => {
      try {
        if (isMounted) {
          await fetchSettings();
        }
      } catch (error) {
        console.error("Error fetching settings:", error);
      }
    };

    loadSettings();

    return () => {
      isMounted = false;
      setIsLoading(false);
    };
  }, [fetchSettings, setIsLoading]);

  const isAnalyticsTab = currentTab === "analytics";

  return (
    <main className={`flex flex-col w-full gap-4 flex-1 min-h-0 ${isAnalyticsTab ? "max-w-full" : "max-w-xlScreen"}`}>
      <Tabs defaultValue="heygen-avatar" value={currentTab} onValueChange={setCurrentTab} className="w-full flex gap-6 flex-1 min-h-0" orientation="vertical">
        <div className={"relative p-4 rounded-md flex flex-col w-fit bg-muted gap-6 shrink-0"}>
          <TabsList className="flex justify-start flex-col w-48 items-start gap-2">
            <TabsTrigger value="heygen-avatar" className="w-full justify-start">{t("tabs.heygenAvatar")}</TabsTrigger>
            <TabsTrigger value="realtimeAPI" className="w-full justify-start">{t("tabs.realtimeAPI")}</TabsTrigger>
            <TabsTrigger value="interactiveSession" className="w-full justify-start">{t("tabs.interactiveSession")}</TabsTrigger>
            {useKnowledgeBase && <TabsTrigger value="knowledge-base" className="w-full justify-start">{t("tabs.knowledgeBase")}</TabsTrigger>}
            {featureFlags.schadenKI && useSchadenKI && <TabsTrigger value="schaden-ki" className="w-full justify-start">{t("tabs.schadenKI")}</TabsTrigger>}
            {featureFlags.blockbrain && <TabsTrigger value="blockbrain" className="w-full justify-start">{t("tabs.blockbrain")}</TabsTrigger>}
            <TabsTrigger value="branding" className="w-full justify-start">{t("tabs.branding")}</TabsTrigger>
            {featureFlags.analytics && <TabsTrigger value="analytics" className="w-full justify-start">{t("tabs.analytics", "Statistiken")}</TabsTrigger>}
            <TabsTrigger value="heygen-account" className="w-full justify-start">{t("tabs.heygenAccount")}</TabsTrigger>
          </TabsList>
          <div>
            {(currentTab !== "heygen-account" && currentTab !== "knowledge-base" && currentTab !== "schaden-ki" && currentTab !== "blockbrain" && currentTab !== "analytics") && (
              <SaveSettingsButton />
            )}
            {currentTab === "heygen-account" && (
              <SaveApiKeyButton />
            )}
            {currentTab === "schaden-ki" && (
              <SaveSchadenKIButton />
            )}
            {currentTab === "blockbrain" && (
              <SaveBlockbrainButton />
            )}
          </div>
          <span className="absolute bottom-2 text-sm text-gray-300">{env.VITE_VERSION_AND_DEPLOYMENT_DATETIME}</span>
        </div>

        <TabsContent value="heygen-avatar" className="mt-0 space-y-4 overflow-y-auto flex-1 min-h-0">
          <HeygenAvatarSettings />
        </TabsContent>

        <TabsContent value="realtimeAPI" className="mt-0 space-y-4 overflow-y-auto flex-1 min-h-0">
          <RealtimeApiSettings />
        </TabsContent>

        <TabsContent value="interactiveSession" className="mt-0 space-y-4 overflow-y-auto flex-1 min-h-0">
          <InteractiveSessionSettings />
        </TabsContent>

        {useKnowledgeBase && (
          <TabsContent value="knowledge-base" className="mt-0 space-y-4 overflow-y-auto flex-1 min-h-0">
            <KnowledgeBaseSettings />
          </TabsContent>
        )}

        {featureFlags.schadenKI && useSchadenKI && (
          <TabsContent value="schaden-ki" className="mt-0 space-y-4 overflow-y-auto flex-1 min-h-0">
            <SchadenKISettings />
          </TabsContent>
        )}

        {featureFlags.blockbrain && (
          <TabsContent value="blockbrain" className="mt-0 space-y-4 overflow-y-auto flex-1 min-h-0">
            <BlockbrainSettings />
          </TabsContent>
        )}

        <TabsContent value="branding" className="mt-0 space-y-4 overflow-y-auto flex-1 min-h-0">
          <BrandingSettings />
        </TabsContent>

        {featureFlags.analytics && (
          <TabsContent value="analytics" className="mt-0 space-y-4 overflow-y-auto flex-1 min-h-0 max-w-none">
            <AnalyticsSettings />
          </TabsContent>
        )}

        <TabsContent value="heygen-account" className="mt-0 space-y-4 overflow-y-auto flex-1 min-h-0">
          <InfoSection />
        </TabsContent>
      </Tabs>
    </main>
  );
}